import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import './LegalPage.css';

function DisclaimerPage() {
  const { t } = useTranslation();

  return (
    <div className="legal-page">
      <SEO page="disclaimer" />
      <Header />

      <div className="legal-container">
        <div className="legal-content">
          <h1 className="legal-title">{t('legal.disclaimer.title', 'Disclaimer')}</h1>
          <p className="legal-updated">{t('legal.disclaimer.lastUpdated', 'Last updated')}: January 2025</p>

          <section className="legal-section legal-important">
            <h2>{t('legal.disclaimer.importantTitle', 'Important Notice')}</h2>
            <p>{t('legal.disclaimer.importantText', 'Please read this disclaimer carefully before using TraderMap. By using our website, you acknowledge that you have read, understood, and agree to this disclaimer.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.noAdviceTitle', 'No Financial Advice')}</h2>
            <p>{t('legal.disclaimer.noAdviceText1', 'The information provided on TraderMap, including but not limited to whale trade alerts, price data, market analysis, and any other content, is for general informational purposes only.')}</p>
            <p>{t('legal.disclaimer.noAdviceText2', 'Nothing on this website constitutes, or is intended to constitute, financial advice, investment advice, trading advice, or any other kind of professional advice. We are not financial advisors, investment advisors, or trading experts.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.riskTitle', 'Risk Warning')}</h2>
            <p>{t('legal.disclaimer.riskText1', 'Cryptocurrency trading involves substantial risk of loss and is not suitable for every investor. The value of cryptocurrencies can fluctuate dramatically and may result in significant losses.')}</p>
            <p>{t('legal.disclaimer.riskText2', 'You should carefully consider whether trading or investing in cryptocurrencies is suitable for you in light of your financial condition. Never invest money you cannot afford to lose.')}</p>
            <ul>
              <li>{t('legal.disclaimer.risk1', 'Cryptocurrencies are highly volatile and speculative')}</li>
              <li>{t('legal.disclaimer.risk2', 'Past performance does not guarantee future results')}</li>
              <li>{t('legal.disclaimer.risk3', 'You could lose all of your invested capital')}</li>
              <li>{t('legal.disclaimer.risk4', 'Markets can move against you rapidly')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.accuracyTitle', 'Data Accuracy')}</h2>
            <p>{t('legal.disclaimer.accuracyText', 'While we strive to provide accurate and up-to-date information, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information on our website. Any reliance you place on such information is strictly at your own risk.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.whaleTitle', 'Whale Trade Data')}</h2>
            <p>{t('legal.disclaimer.whaleText1', 'Whale trade alerts and large transaction data displayed on TraderMap are sourced from various exchanges and may not represent the complete picture of market activity.')}</p>
            <p>{t('legal.disclaimer.whaleText2', 'Large trades can occur for many reasons unrelated to market sentiment, including:')}</p>
            <ul>
              <li>{t('legal.disclaimer.whale1', 'Internal transfers between wallets')}</li>
              <li>{t('legal.disclaimer.whale2', 'Exchange cold storage movements')}</li>
              <li>{t('legal.disclaimer.whale3', 'OTC (over-the-counter) settlements')}</li>
              <li>{t('legal.disclaimer.whale4', 'Market making activities')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.delaysTitle', 'Data Delays')}</h2>
            <p>{t('legal.disclaimer.delaysText', 'Market data displayed on TraderMap may be delayed. Real-time data availability depends on exchange APIs and network conditions. Do not rely on our data for time-sensitive trading decisions.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.thirdPartyTitle', 'Third-Party Information')}</h2>
            <p>{t('legal.disclaimer.thirdPartyText', 'TraderMap may display data and information from third-party sources, including cryptocurrency exchanges and data providers. We are not responsible for the accuracy or reliability of third-party data.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.decisionTitle', 'Your Responsibility')}</h2>
            <p>{t('legal.disclaimer.decisionText1', 'Any decisions you make based on information found on TraderMap are solely your responsibility. We strongly recommend that you:')}</p>
            <ul>
              <li>{t('legal.disclaimer.decision1', 'Conduct your own research (DYOR)')}</li>
              <li>{t('legal.disclaimer.decision2', 'Consult with qualified financial professionals')}</li>
              <li>{t('legal.disclaimer.decision3', 'Never invest more than you can afford to lose')}</li>
              <li>{t('legal.disclaimer.decision4', 'Understand the risks before trading')}</li>
              <li>{t('legal.disclaimer.decision5', 'Use multiple sources to verify information')}</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.liabilityTitle', 'Limitation of Liability')}</h2>
            <p>{t('legal.disclaimer.liabilityText', 'Under no circumstances shall TraderMap, its operators, employees, or affiliates be liable for any direct, indirect, incidental, special, or consequential damages resulting from your use of or inability to use our website, including but not limited to losses arising from trading activities influenced by information on our platform.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.regulatoryTitle', 'Regulatory Notice')}</h2>
            <p>{t('legal.disclaimer.regulatoryText', 'TraderMap is not a registered broker, dealer, investment advisor, or trading platform. We do not facilitate trading or hold any user funds. Cryptocurrency regulations vary by jurisdiction, and it is your responsibility to ensure compliance with applicable laws in your region.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.changesTitle', 'Changes to Disclaimer')}</h2>
            <p>{t('legal.disclaimer.changesText', 'We reserve the right to modify this disclaimer at any time. Your continued use of TraderMap after any changes indicates your acceptance of the updated disclaimer.')}</p>
          </section>

          <section className="legal-section">
            <h2>{t('legal.disclaimer.contactTitle', 'Contact Us')}</h2>
            <p>{t('legal.disclaimer.contactText', 'If you have any questions about this Disclaimer, please contact us at:')}</p>
            <p className="legal-contact">
              <a href="mailto:support@tradermap.io">support@tradermap.io</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default DisclaimerPage;
